'use client';

import {
  Box,
  Button,
  SystemIcon,
  SystemIconName,
  TextFieldDropdown,
  Typography,
} from '@wishket/design-system';
import { ChangeEvent, FormEvent, MouseEvent, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon, IconName, WideRadioCardGroup } from '@/shared/Components';
import { getScrollbarStyle, scrollToBottom } from '@/shared/utils';

const START_HOUR = 8;
const END_HOUR = 20;

type Item = {
  key: number | string;
  value: string;
  leadingIcon?: SystemIconName;
};

const generateTimeOptions = () => {
  const timeOptions = Array.from(
    { length: END_HOUR - START_HOUR + 1 },
    (_, index) => {
      const hour = START_HOUR + index;
      const timePeriod = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = String(hour > 12 ? hour - 12 : hour).padStart(
        2,
        '0'
      );
      const formattedTime = hour * 100;
      return [
        {
          key: formattedTime,
          value: `매일 ${formattedHour}:00 ${timePeriod}`,
        },
        {
          key: formattedTime + 30,
          value: `매일 ${formattedHour}:30 ${timePeriod}`,
        },
      ];
    }
  ).flatMap((x) => x);

  return timeOptions;
};

const RADIO_OPTIONS: {
  label: string;
  value: string;
  isSelected: boolean;
  leadingIcon: IconName;
}[] = [
  {
    value: 'morning',
    label: '출근길',
    leadingIcon: 'sun',
    isSelected: false,
  },
  {
    value: 'evening',
    label: '퇴근길',
    leadingIcon: 'moon',
    isSelected: false,
  },
  {
    value: 'snack',
    label: '간식',
    leadingIcon: 'cookie',
    isSelected: false,
  },
  {
    value: 'restroom',
    label: '화장실',
    leadingIcon: 'secret_face',
    isSelected: false,
  },
];

interface NotificationNewsLetterFormProps {
  isOnBoarding?: boolean;
  data: {
    habit: string;
    time: string;
  };
  onChange: {
    habit: (e: ChangeEvent<HTMLInputElement>) => void;
    time: (item: Item) => void;
  };
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  onClose?: () => void;
}

const NotificationNewsLetterForm = ({
  isOnBoarding = false,
  data,
  onChange,
  onSubmit,
  onClose,
}: NotificationNewsLetterFormProps) => {
  const timeOptions = generateTimeOptions();
  const getSelectedTime = (data: number): Item | undefined => {
    const time = timeOptions.find((option) => option.key === data);
    return time ? { ...time, leadingIcon: 'medium_wait' } : undefined;
  };
  const formRef = useRef<HTMLDivElement>(null);

  const closeModal = (e: MouseEvent<HTMLButtonElement>) => {
    if (!onClose) return;
    e.preventDefault();
    onClose();
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex h-full w-full flex-col justify-between"
      data-testid="features-notification-newsletter-form"
    >
      <Box>
        {isOnBoarding ? (
          <Box className="border-b px-5 pb-[23px] pt-6 font-medium text-w-gray-900 desktop:px-8">
            <Box className="flex items-center gap-1">
              <Typography variant="subTitle2">입력을 완료했어요!</Typography>
              <Icon name="confetti" />
            </Box>
            <Typography variant="subTitle2">
              요즘IT와 함께 성장 습관을 만들어 보세요.
            </Typography>
          </Box>
        ) : (
          <Box className="hidden border-b px-8 py-6 desktop:block">
            <Box className="flex items-center justify-between">
              <Typography
                variant="subTitle2"
                className="font-medium text-w-gray-900"
              >
                알림 수정
              </Typography>
              <button onClick={closeModal}>
                <SystemIcon
                  name="large_delete"
                  className="cursor-pointer text-w-gray-600"
                />
              </button>
            </Box>
          </Box>
        )}
        <Box
          className={twMerge(
            'flex flex-col gap-8 overflow-y-scroll py-6 desktop:h-fit',
            getScrollbarStyle(),
            isOnBoarding ? 'h-[calc(100dvh-186px)]' : 'h-full'
          )}
          ref={formRef}
        >
          <Box className="flex flex-col gap-4">
            <Box className="flex flex-col gap-1">
              <Box className="flex">
                <Typography variant="body1" className="font-medium">
                  습관&nbsp;
                </Typography>
                <Typography
                  variant="body1"
                  className="font-medium text-primary"
                >
                  *
                </Typography>
              </Box>
              <Typography variant="body2" className="text-w-gray-600">
                {isOnBoarding
                  ? 'IT 콘텐츠를 매일 원하는 시간에 이메일로 받아보세요.'
                  : '매일 업데이트되는 IT 콘텐츠, 원하는 시간에 이메일로 받아보세요.'}
              </Typography>
            </Box>

            <WideRadioCardGroup
              onChange={onChange.habit}
              selectedValue={data.habit}
              items={RADIO_OPTIONS}
            />
          </Box>
          <Box className="flex flex-col gap-4">
            <Box className="flex">
              <Typography variant="body1" className="font-medium">
                시간&nbsp;
              </Typography>
              <Typography variant="body1" className="font-medium text-primary">
                *
              </Typography>
            </Box>
            <Box onClick={() => scrollToBottom(formRef)}>
              <TextFieldDropdown
                selectedItem={getSelectedTime(Number(data.time))}
                items={timeOptions}
                onItemClick={onChange.time}
                placeholder="시간을 선택해 주세요"
                className="text-w-gray-600"
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        className={twMerge(
          'flex gap-4 border-t px-5 pb-4 pt-[15px] desktop:px-8 desktop:py-5',
          !isOnBoarding && 'w-full justify-end'
        )}
      >
        {isOnBoarding ? (
          <Button variant="solid" className="w-full" type="submit">
            <Typography variant="body1">요즘IT 시작하기</Typography>
          </Button>
        ) : (
          <>
            <Button
              className="w-full border-w-bluegray-200 bg-w-bluegray-200 active:border-w-bluegray-300 active:bg-w-bluegray-300 hover:border-w-bluegray-300 hover:bg-w-bluegray-300 desktop:w-[89px]"
              variant="solid"
              onClick={closeModal}
            >
              닫기
            </Button>
            <Button
              className="w-full desktop:w-[89px]"
              variant="solid"
              type="submit"
            >
              수정하기
            </Button>
          </>
        )}
      </Box>
    </form>
  );
};

export default NotificationNewsLetterForm;
